import {QueryClient, QueryClientProvider} from '@tanstack/react-query';
import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {BrowserRouter} from 'react-router-dom';

import PageLayoutEnum from './enum/pageLayout';
import ErrorPage from './pages/ErrorPage/ErrorPage';
import Welcome from './pages/Welcome/Welcome';
import PageRouter from './routes/PageRouter';
import {getProfileApi} from './service/auth';
import {getMongoConfig} from './service/config';
import getCommonFilterValues from './service/getCommonFilters';
import {setUser} from './store/actions/auth';
import {setCommonFilterAction} from './store/actions/filters';
import {setPermissions} from './store/actions/permissions';
import {getDerivedUserData} from './utils/auth';
import {handleError} from './utils/errors';
import {logOutUser} from './utils/helper';
import {createPermissions} from './utils/permission';
import StorageUtils from './utils/storage';
import RoutesConstant from './constants/routes';

export default function AuthenticatedRoutes() {
  const tokenKey = 't';

  const param = new URLSearchParams(window.location.search);

  const dispatch = useDispatch();

  const [loginError, setLoginError] = useState(null);

  const user = useSelector((state) => state.auth);

  const permissions = useSelector((state) => state.permissions);

  const commonFilters = useSelector((state) => state.commonFilters);

  const [token, setToken] = useState(param.get(tokenKey) ? param.get(tokenKey) : user?.token ? btoa(user.token) : null);

  const [isHomePage, setIsHomePage] = useState(window.location.pathname === RoutesConstant.HOME && !user?.isAuthenticated ? true : false);
  const removeTokenFromUrl = () => {
    param.delete(tokenKey);
    const newParam = param.toString();
    let redirectTo = window.location.pathname;
    if (newParam) {
      redirectTo += `?${param.toString()}`;
    }
    window.history.replaceState({}, document.title, redirectTo);
    setToken(null);
  };

  const getProfile = async () => {
    StorageUtils.setUser(null);
    const response = await getProfileApi({token});
    const responseData = response.data?.data || {};
    const derivedData = getDerivedUserData(responseData?.user?.permissionList);
    const userData = {
      ...responseData?.user,
      derivedData: derivedData,
    };
    const permissionObj = createPermissions(responseData.user?.permissionList);

    setUser(dispatch, userData);
    setPermissions(dispatch, permissionObj);
    if (token) removeTokenFromUrl();
  };

  const setCommonFilterValues = async () => {
    const data = await getCommonFilterValues({fetchAll: true, addDefaultFilter: 'NA'});
    setCommonFilterAction(dispatch, data);
  };

  useEffect(() => {
    (async () => {
      try {
        await getProfile();
        await getMongoConfig();
        await setCommonFilterValues();
      } catch (error) {
        setLoginError(handleError(error));
      }
    })();
  }, []);

  if (loginError) {
    return <ErrorPage message={loginError} size="large" />;
  } else if (!token && user.isAuthenticated && permissions.isSet && commonFilters.isSet) {
    const queryClient = new QueryClient();
    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <PageRouter />
        </BrowserRouter>
      </QueryClientProvider>
    );
  } else if (token || user.isAuthenticated || isHomePage) {
    return <Welcome isLoading={true} layout={PageLayoutEnum.FULL_WIDTH} />;
  } else {
    logOutUser();
  }
}
